<template>
  <div>
    <ValidationObserver ref="form-user">
      <header-slot>
        <template v-slot:content-right>
          <div>
            <b-button
              v-if="$route.params.id"
              variant="outline-warning"
              class="mr-1"
              @click="updateUser"
            >
              {{ $t("UPDATE") }}
            </b-button>
            <b-button
              v-else
              variant="outline-primary"
              class="mr-1"
              :disabled="invalid"
              @click="registerUser()"
            >
              {{ $t("REGISTER") }}
            </b-button>
            <b-button
              class="mr-1"
              variant="outline-danger"
              :to="{ name: 'users-list' }"
            >
              {{ $t("CANCEL") }}
            </b-button>
          </div>
        </template>
      </header-slot>

      <!-- User Information -->
      <b-row>
        <b-col cols="12">
          <b-card :title="$t('user information') | capitalize">
            <b-container class="mx-1 pr-3 my-1">
              <b-row>
                <!-- User Info: Left col -->
                <b-col
                  xl="4"
                  lg="4"
                  md="12"
                  class="d-flex justify-content-between"
                >
                  <!-- User Avatar & Action Buttons -->
                  <div class="d-flex justify-content-start">
                    <div
                      :class="['avatar-container', { backdrop: form.remove }]"
                      v-if="form.file != ''"
                    >
                      <div
                        class="remove-temporal-avatar"
                        v-if="form.remove"
                        @click="clearFileData"
                      >
                        <div>
                          <feather-icon icon="XIcon" size="35"></feather-icon>
                        </div>
                      </div>
                      <b-avatar
                        ref="previewEl"
                        :src="form.file"
                        :text="avatarText(userData.fullName)"
                        size="130px"
                        rounded
                      />
                    </div>
                    <b-avatar
                      v-else
                      :src="userData.avatar"
                      :text="avatarText(userData.fullName)"
                      :variant="`light-primary`"
                      size="130px"
                      rounded
                    />
                    <div class="ml-2">
                      <div class="mb-1">
                        <h4 class="mb-0">
                          <b>
                            {{ userData.fullName }}
                          </b>
                        </h4>
                        <span class="card-text">{{ userData.email }}</span>
                      </div>

                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="addFile"
                      >
                        <b-form-file
                          ref="refInputEl"
                          class="d-none"
                          @change="fillFileData"
                        />
                        <feather-icon icon="ImageIcon" class="d-inline mr-25" />
                        <span class="d-none d-sm-inline">Upload</span>
                      </b-button>
                    </div>
                  </div>
                </b-col>

                <b-col xl="8" lg="8">
                  <b-row>
                    <b-col xl="8" lg="8">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="user_type"
                        :rules="userId ? 'required' : ''"
                      >
                        <b-form-group
                          label-cols-lg="4"
                          label-cols-md="5"
                          label-cols-sm="4"
                          label-cols="12"
                          content-cols-lg="7"
                          content-cols-md="7"
                          content-cols-sm="8"
                          content-cols="12"
                        >
                          <template #label>
                            <feather-icon
                              icon="GitPullRequestIcon"
                              class="mr-75"
                            />
                            {{ $t("User Type") }}
                          </template>
                          <b-form-radio-group
                            id="user_type"
                            :state="errors[0] ? false : null"
                            class="text-danger"
                            style="margin-top: 6px"
                            v-model="userData.type"
                            :options="userTypes"
                          />
                        </b-form-group>
                      </ValidationProvider>
                    </b-col>

                    <b-col xl="8" lg="8" v-if="userData.type == 2">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="employee_id"
                        rules="required"
                      >
                        <b-form-group
                          label-cols-lg="4"
                          label-cols-md="5"
                          label-cols-sm="4"
                          label-cols="12"
                          content-cols-lg="8"
                          content-cols-md="7"
                          content-cols-sm="8"
                          content-cols="12"
                          :state="errors[0] ? false : null"
                        >
                          <template #label>
                            <feather-icon icon="UserIcon" class="mr-75" />
                            {{ $t("Employee") }}
                          </template>
                          <v-select
                            id="employee_id"
                            v-model="userData.employee_id"
                            :options="employees"
                            label="label"
                            :reduce="(dt) => dt.id"
                          />
                        </b-form-group>
                      </ValidationProvider>
                    </b-col>

                    <b-col xl="6" lg="6" v-if="userData.type == 1">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="first_name"
                        rules="required"
                      >
                        <b-form-group
                          label-cols-lg="4"
                          label-cols-md="5"
                          label-cols-sm="4"
                          label-cols="12"
                          content-cols-lg="8"
                          content-cols-md="7"
                          content-cols-sm="8"
                          content-cols="12"
                        >
                          <template #label>
                            <feather-icon icon="UserIcon" class="mr-75" />
                            {{ $t("First Name") }}
                          </template>
                          <b-form-input
                            :state="errors[0] ? false : null"
                            :class="{ 'is-border-danger': errors[0] }"
                            v-model="userData.first_name"
                          />
                        </b-form-group>
                      </ValidationProvider>
                    </b-col>

                    <b-col xl="6" lg="6" v-if="userData.type == 1">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="last_name"
                        rules="required"
                      >
                        <b-form-group
                          label-cols-lg="4"
                          label-cols-md="5"
                          label-cols-sm="4"
                          label-cols="12"
                          content-cols-lg="8"
                          content-cols-md="7"
                          content-cols-sm="8"
                          content-cols="12"
                        >
                          <template #label>
                            <feather-icon icon="UserIcon" class="mr-75" />
                            {{ $t("Last Name") }}
                          </template>

                          <b-form-input
                            :state="errors[0] ? false : null"
                            :class="{ 'is-border-danger': errors[0] }"
                            v-model="userData.last_name"
                          />
                        </b-form-group>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-container>
          </b-card>
        </b-col>
      </b-row>

      <!-- User Access -->
      <b-row>
        <b-col xl="10" lg="10" sm="12" xs="12" cols="12">
          <b-card :title="$t('user access') | capitalize">
            <b-row class="mt-2">
              <!-- User Name -->
              <b-col md="6" xs="12" cols="12">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="user_name"
                  rules="required"
                >
                  <b-form-group
                    label-cols-lg="3"
                    label-cols-md="3"
                    label-cols-sm="3"
                    label-cols="12"
                    content-cols-lg="9"
                    content-cols-md="9"
                    content-cols-sm="9"
                    content-cols="12"
                  >
                    <template #label> {{ $t("User Name") }} </template>
                    <b-form-input
                      :state="errors[0] ? false : null"
                      :class="{
                        'is-border-danger': errors[0] || isValidEmail,
                      }"
                      v-model="userData.user_name"
                      id="user_name"
                    />
                    <b-form-invalid-feedback>
                      <span v-if="errors[0]">User Name {{ errors[0] }}</span>
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col md="6" xs="12" cols="12">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="email"
                  rules="required"
                >
                  <b-form-group
                    label-cols-lg="3"
                    label-cols-md="3"
                    label-cols-sm="3"
                    label-cols="12"
                    content-cols-lg="9"
                    content-cols-md="9"
                    content-cols-sm="9"
                    content-cols="12"
                  >
                    <template #label> Email </template>
                    <b-form-input
                      v-model="userData.email"
                      :state="errors[0] ? false : null"
                      :class="{
                        'is-border-danger': errors[0] || isValidEmail,
                      }"
                    />
                    <b-form-invalid-feedback>
                      <span v-if="errors[0]">New Email {{ errors[0] }}</span>
                    </b-form-invalid-feedback>
                    <span class="text-danger" v-if="isValidEmail"
                      >Email already exists</span
                    >
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col md="6" v-if="!$route.params.id">
                <b-form-group
                  label-cols-lg="3"
                  label-cols-md="3"
                  label-cols-sm="3"
                  label-cols="12"
                  content-cols-lg="9"
                  content-cols-md="9"
                  content-cols-sm="9"
                  content-cols="12"
                >
                  <template #label> Password </template>
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="password"
                    rules="required"
                  >
                    <b-form-input
                      v-model="userData.password"
                      type="password"
                      :state="errors[0] ? false : null"
                      :class="{ 'is-border-danger': errors[0] }"
                    />
                    <b-form-invalid-feedback>
                      New Password: {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </ValidationProvider>
                </b-form-group>
              </b-col>

              <!-- Type Role -->
              <b-col md="6">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="type_role"
                  rules="required"
                >
                  <b-form-group
                    style="margin-bottom: 0"
                    label-cols-lg="2"
                    label-cols-md="5"
                    label-cols-sm="4"
                    label-cols="12"
                    content-cols-lg="7"
                    content-cols-md="7"
                    content-cols-sm="8"
                    content-cols="12"
                    :state="errors[0] ? false : null"
                  >
                    <template #label> Rol </template>
                    <b-form-radio-group
                      class="text-danger d-flex justify-content-around"
                      value-field="value"
                      text-field="text"
                      style="margin-top: 6px"
                      v-model="userData.type_role"
                      @change="changeTypeRole"
                      :options="typeRoles"
                    />
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col md="6" xs="12" cols="12" v-if="userData.type_role == 2">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="role_id"
                  rules="required"
                >
                  <b-form-group :state="errors[0] ? false : null">
                    <div class="d-flex justify-content-between text-white">
                      <label for="user-role" v-t="'Role'"></label>
                    </div>
                    <v-select
                      small
                      v-model="userData.hr_role_id"
                      :options="listRoles"
                      :reduce="(val) => val.id"
                      label="label"
                      class="w-100"
                      hint="Select a role"
                    />
                  </b-form-group>
                </ValidationProvider>
              </b-col>

            </b-row>
          </b-card>
        </b-col>
        <b-col cols="12" lg="2" class="d-flex align-items-stretch">
          <b-card class="w-100 d-flex justify-content-center" :title="$t('others') | capitalize">
            <!-- User Status -->
            <b-form-group class="d-flex align-items-center">
              <b-form-checkbox
                v-model="userData.status"
                class="d-inline-block"
                value="1"
                unchecked-value="0"
              ></b-form-checkbox>
              <span>Active</span>
            </b-form-group>
            <b-form-group class="d-flex align-items-center">
              <b-form-checkbox
                v-model="userData.permission_app"
                class="d-inline-block"
                value="1"
                unchecked-value="0"
              ></b-form-checkbox>
              <span>Permission App</span>
            </b-form-group>

            <div
              @click="showSetAttendanceAvailableDays"
              v-if="isCoordinator"
              class="cursor-pointer"
            >
              <feather-icon icon="CalendarIcon" />
              <span class="ml-1">Attendance Days</span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
            <user-sessions :user_id="userId"></user-sessions>
        </b-col>
      </b-row>
    </ValidationObserver>

    <ModalAttendanceAvailableDays
      v-if="showModalAttendanceAvailableDays"
      @hideModal="showModalAttendanceAvailableDays = false"
      :user="userData"
    />
  </div>
</template>
<script>
import moment from "moment";
import { avatarText } from "@core/utils/filter";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import UserServices from "@/views/users/services/users.service";
import RolesAreaService from "@/views/settings/views/roles-area/service/roles-area.service.js";
import Avatar from "@/assets/images/avatars/1.png";
import ModalAttendanceAvailableDays from "@/views/users/views/list/components/AttendanceAvailableDays.vue";
import UserSessions from "@/views/users/views/list/components/UserSessions.vue";

export default {
  mixins: [togglePasswordVisibility],
  components: {
    ModalAttendanceAvailableDays,
    UserSessions
  },
  data() {
    return {
      imagePreviewUrl: Avatar,
      loading: false,
      userData: {
        id: "",
        type: null,
        employee_id: null,
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        status: false,
        role_id: null,
        hr_role_id: null,
        typesenior: "",
        rolenew: null,
        file: "",
        image: "",
        super: "",
        extension: "",
        type_role: null,
        newarr: [],
      },
      isInitialLoad: true,
      form: {
        file: "",
        image: "",
        remove: false,
      },
      user: {
        login_type: "local",
      },
      typeRoles: [
        { value: 1, text: "CEO" },
        { value: 2, text: "Others" },
      ],
      isValidEmail: false,
      listCampus: [],
      listRoles: [],
      selectedModules: [],
      modulesOptions: [],
      selectedSections: [],
      moduleSections: [],
      listPersonals: [],
      showModalAttendanceAvailableDays: false,
      userTypes: [
        { value: 1, text: "Client" },
        { value: 2, text: "Personal" },
      ],
      employees: [],
    };
  },
  async created() {
    try {
      this.addPreloader();
      await this.getModules();
      await Promise.all([
        this.getCampus(),
        this.getRoles(),
        this.getEmployees(),
        this.getInfoEmployee(),
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      this.removePreloader();
    }
  },
  computed: {
    isCoordinator() {
      return this.userData.role_id == 4;
    },
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    userId() {
      return this.$route.params.id;
    },
    inputDisable() {
      return this.user.type == 1 ? true : false;
    },
    routeModule() {
      return this.$route.meta.route;
    },
    availableModulesOptions() {
      const options = this.modulesOptions.filter(
        (op) => this.selectedModules.indexOf(op.id) != -1
      );
      return options;
    },
  },
  methods: {
    async getInfoEmployee() {
      try {
        if (this.$route.params.id) {
          const { data } = await UserServices.getUserEdit({
            id: this.$route.params.id,
          });
          this.userData.id = this.$route.params.id;
          this.userData.type = data[0].type;
          this.userData.type_role = data[0].type_role;
          this.userData.employee_id = data[0].employee_id;
          this.userData.email = data[0].email;
          this.userData.user_name = data[0].user_name;
          this.userData.role_id = data[0].role_id;
          this.userData.hr_role_id = data[0].hr_role_id;
          this.userData.status = data[0].status;
          this.userData.permission_app = data[0].permission_app

          this.selectedSections = data[0].newarr || [];
          
          // Populate derived modules
          const moduleIds = [];
          this.selectedSections.forEach((s) => {
            this.modulesOptions.forEach((mod) => {
              if (mod.section && mod.section.find((sec) => sec.value == s.id)) {
                if (!moduleIds.includes(mod.id)) moduleIds.push(mod.id);
              }
            });
          });
          this.selectedModules = moduleIds;
          this.inputSelectModule();
        }
      } catch (error) {
        console.log(error);
      }
    },
    changeTypeRole() {
      if (this.userData.type_role == 2) {
        this.userData.hr_role_id = null;
        this.userData.role_id = 2; // Default system role
      }
    },
    deselectedOption(event) {
      const sections = event.section;
      const newSections = [];
      this.selectedSections.map((row) => {
        const exist = sections.find((row2) => row2.value == row.id);
        if (!exist) {
          newSections.push(row);
        }
      });
      this.selectedSections = newSections;
    },
    inputSelectModule() {
      const newModuleSections = [];
      this.selectedModules.map((row) => {
        const modul = this.modulesOptions.filter((row2) => row2.id == row);
        newModuleSections.push(modul[0]);
      });
      this.moduleSections = newModuleSections;
    },
    avatarText(fullName) {
      return avatarText(fullName);
    },
    addFile() {
      this.$refs.refInputEl.$el.childNodes[0].click();
    },
    fillFileData(event) {
      let image = event.srcElement.files[0];
      this.form.image = image.name;

      let reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onload = () => (this.form.file = reader.result);
      this.form.remove = true;
    },
    previewImage(event) {
      if (event.target.files[0]) {
        this.imagePreviewUrl = URL.createObjectURL(event.target.files[0]);
      }
    },

    changeCheckAll(index) {
      this.$refs[`checkbox-${index}`][0].localChecked = "not_aceepted";
    },

    selectAllSection(event, section) {
      const module = this.modulesOptions.find(
        (option) => option.id == section
      );
      const sectionsId = module.section.map((element) => element.value);

      for (let i = 0; i < sectionsId.length; i++) {
        const sectionId = sectionsId[i];
        const index = this.selectedSections.findIndex(s => s.id === sectionId);

        if (event == "accepted") {
          if (index === -1) {
            this.selectedSections.push({
              id: sectionId,
              can_view: 1,
              can_create: 1,
              can_update: 1,
              can_delete: 1,
            });
          } else {
            this.selectedSections[index].can_view = 1;
            this.selectedSections[index].can_create = 1;
            this.selectedSections[index].can_update = 1;
            this.selectedSections[index].can_delete = 1;
          }
        } else {
          if (index !== -1) {
            this.selectedSections.splice(index, 1);
          }
        }
      }
    },
    hasPermission(sectionId, type) {
      const section = this.selectedSections.find(s => s.id === sectionId);
      if (!section) return false;
      return !!section[type];
    },
    togglePermission(sectionId, type) {
      const index = this.selectedSections.findIndex(s => s.id === sectionId);
      if (index !== -1) {
        this.selectedSections[index][type] = this.selectedSections[index][type] ? 0 : 1;
        
        // If all permissions are removed, should we remove the section? 
        // Or keep it? Usually, if can_view is off, others should probably be off too.
        const s = this.selectedSections[index];
        if (!s.can_view && !s.can_create && !s.can_update && !s.can_delete) {
            this.selectedSections.splice(index, 1);
        }
      } else {
        const newSection = {
          id: sectionId,
          can_view: 0,
          can_create: 0,
          can_update: 0,
          can_delete: 0,
        };
        newSection[type] = 1;
        // If any granular permission is enabled, can_view should probably be enabled too
        if (type !== 'can_view') newSection.can_view = 1;
        
        this.selectedSections.push(newSection);
      }
    },
    async getCampus() {
      const { data, status } = await UserServices.getCampus();
      this.listCampus = data;
    },
    async getRoles() {
      const { data } = await RolesAreaService.getRolesForSelect();
      this.listRoles = data;
    },
    async applyRolePermissions(roleId) {
      try {
        this.addPreloader();
        const { data } = await RolesAreaService.getRoleById({ id: roleId });
        if (data.length > 0 && data[0].sections) {
          const roleSections = data[0].sections;
          this.selectedSections = roleSections;
          
          // Re-calculate selectedModules
          const moduleIds = [];
          this.selectedSections.forEach((s) => {
            this.modulesOptions.forEach((mod) => {
              if (mod.section && mod.section.find((sec) => sec.value == s.id)) {
                if (!moduleIds.includes(mod.id)) moduleIds.push(mod.id);
              }
            });
          });
          this.selectedModules = moduleIds;
          this.inputSelectModule();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.removePreloader();
      }
    },
    async getModules() {
      const { data } = await UserServices.getModules();
      this.modulesOptions = data;
    },
    async getEmployees() {
      try {
        const { data } = await UserServices.getPersonals();
        this.employees = data;
      } catch (error) {
        console.log(error);
      }
    },
    async updateUser() {
      try {
        const result = await this.$refs["form-user"].validate();
        if (result) {
          const confirm = await this.showConfirmSwal();
          if (confirm.isConfirmed) {
            this.addPreloader();
            this.userData.newarr = this.selectedSections;
            const data = await UserServices.updateUser(this.userData);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.removePreloader();
        this.$nextTick(() => {
          this.isInitialLoad = false;
        });
      }
    },
    watch: {
      "userData.hr_role_id": function (newVal) {
        if (!this.isInitialLoad && newVal) {
          this.userData.role_id = 2; // Set system role to OTHERS
        }
      },
    },
    async checkFields() {
      try {
        const response = await this.$refs.checkForm.validate();
        if (response) {
          await this.registerUser();
        }
      } catch (error) {
        console.log(error);
      }
    },

    async registerUser() {
      this.loading = true;
      try {
        this.addPreloader();
        this.user.sections = this.selectedSections;
        const { data, status } = await UserServices.register(this.user);
      } catch (error) {
        this.removePreloader();
        console.log(error);
      } finally {
        this.removePreloader();
        this.loading = false;
      }
    },

    showSetAttendanceAvailableDays() {
      this.showModalAttendanceAvailableDays = true;
    },
  },
  watch: {
    // "user.personal_id"(newValue) {
    //   if (newValue != null) {
    //     const personal = this.listPersonals.filter(
    //       (personal) => personal.id === newValue
    //     );
    //     this.user.first_name = personal[0].first_name;
    //     this.user.last_name = personal[0].last_name;

    //     return;
    //   }

    //   this.user.first_name = null;
    //   this.user.last_name = null;
    // },

    selectedModules(newValue) {
      if (this.selectedModules.length > 0) {
        return;
      }
      this.selectedSections = [];
    },
  },
};
</script>


<style lang="scss">
.avatar-container {
  position: relative;

  .remove-temporal-avatar {
    display: none;
    transition: all 0.5s ease-in-out;
  }

  &.backdrop:hover {
    &::after {
      position: absolute;
      content: "";
      background: grey;
      width: 100%;
      height: 100%;
      opacity: 0.5;
      z-index: 10;
      top: 0px;
      left: 0px;
    }

    .remove-temporal-avatar {
      cursor: pointer;
      position: absolute;
      z-index: 100;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      display: flex !important;
      justify-content: center;
      align-items: center;

      div {
        width: 45px;
        height: 45px;
        background: transparent !important;
        border: 3px white solid;
        outline: none;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        z-index: 100;
      }
    }
  }
}
.checks-boxes {
  display: grid;
  grid-template-columns: auto auto auto;
}
.is-border-danger {
  border: 1px solid red !important;
  border-radius: 0px 4px 4px 0px !important;
}
@media screen and(max-width: 1200px) and (min-width: 968px) {
  .information-form {
    margin-top: 30px;
  }
}
</style>
